/**
 * What a moderator's action on a player's match came to — a ban, a resolved
 * hand, a private chat's start or end — as the game server answers it, and
 * the words the moderator's screen says it in.
 *
 * The game server tells the match and waits for it only briefly: a busy match
 * does the action once it is free (queued), which the screen says rather than
 * calling it done; and an action not done says why — the match has ended, or
 * is too busy to take it — in the moderator's language, where the server's
 * own words are English. Each of the four says it through sayActionOutcome,
 * where each had written the same reading out by hand.
 */

import type { TFunction } from 'i18next';
import type { SignalOutcomeWire } from '../../api/moderator/moderation/types';
import { showToast, type ToastType } from '../../components/toast/toast-store';

/** Done, done once the match is free, or not done. */
type ActionOutcome = 'done' | 'queued' | 'failed';

/** An action's answer, as each of the four is written: its success, the signal, the server's words for a failure. */
interface ActionAnswer {
  success: boolean;
  signal?: SignalOutcomeWire;
  /** The server's words for a failure: `error`, or a ban's `message`. */
  error?: string;
  message?: string;
}

/** The words an action is said in, as keys: done (said only when given), queued, and a failure's title. */
interface ActionWords {
  done?: string;
  queued: string;
  failed: string;
}

/** What the moderator is told of an action: a toast's tone and title, and under a failure why — a key, or the server's words. */
interface ActionNotice {
  tone: ToastType;
  title: string;
  why?: { key: string } | { text: string };
}

/** An action's answer, by its success and what came of telling the match. */
export function actionOutcome(answer: { success: boolean; signal?: SignalOutcomeWire }): ActionOutcome {
  if (!answer.success) return 'failed';
  return answer.signal === 'queued' ? 'queued' : 'done';
}

/**
 * Why an action was not done, when what came of telling the match says it:
 * the match has ended, or was too busy to take it. Null when the match took it
 * and said why itself, or nothing says: the caller says its own.
 */
export function whyNotDone(signal: SignalOutcomeWire | undefined): 'matchModeration.matchEnded' | 'matchModeration.matchBusy' | null {
  switch (signal) {
    case 'no_match':
    case 'ended':
      return 'matchModeration.matchEnded';
    case 'refused':
      return 'matchModeration.matchBusy';
    default:
      return null;
  }
}

/**
 * What the moderator is told of an action's answer: queued, said so; not
 * done, its title over why — in the moderator's words when the match ended or
 * could not take it, else the server's own; done, its words when it has any,
 * or nothing.
 */
export function actionNotice(answer: ActionAnswer, words: ActionWords): ActionNotice | null {
  switch (actionOutcome(answer)) {
    case 'queued':
      return { tone: 'info', title: words.queued };
    case 'failed': {
      const key = whyNotDone(answer.signal);
      const text = answer.error || answer.message;
      return { tone: 'error', title: words.failed, ...(key ? { why: { key } } : text ? { why: { text } } : {}) };
    }
    default:
      return words.done ? { tone: 'success', title: words.done } : null;
  }
}

/** Tells the moderator what came of an action (actionNotice), as a toast, and answers its outcome. */
export function sayActionOutcome(t: TFunction, answer: ActionAnswer, words: ActionWords): ActionOutcome {
  const notice = actionNotice(answer, words);
  if (notice) {
    const why = notice.why && ('key' in notice.why ? t(notice.why.key) : notice.why.text);
    showToast(notice.tone, t(notice.title), why);
  }
  return actionOutcome(answer);
}
